import type { StorefrontLegal } from "@loot-os/api-contract";
import { Button } from "@loot-os/ui/components/button";
import { Input } from "@loot-os/ui/components/input";
import { Label } from "@loot-os/ui/components/label";
import type { ReactNode } from "react";
import { useState } from "react";

/** One field of the identity: its label, an example, and what the policy pages print it into. */
const FIELDS: readonly {
  key: keyof StorefrontLegal;
  label: string;
  placeholder: string;
  help: string;
  type: "text" | "email";
  required: boolean;
}[] = [
  {
    key: "legalName",
    label: "Legal name",
    placeholder: "Acme Cards, Inc.",
    help: "The company the Terms of Service bind shoppers to. The store's display name is added as a trade name when it differs.",
    type: "text",
    required: true,
  },
  {
    key: "supportEmail",
    label: "Support email",
    placeholder: "support@example.com",
    help: "Where both pages send shoppers with questions, disputes and account requests.",
    type: "email",
    required: true,
  },
  {
    key: "legalEmail",
    label: "Legal email",
    placeholder: "Same as the support email",
    help: "Where notices of claimed copyright infringement go. Leave it blank to use the support email.",
    type: "email",
    required: false,
  },
  {
    key: "postalAddress",
    label: "Postal address",
    placeholder: "Acme Cards, Inc., Attn. Legal, 1 Main St, Springfield, IL 62701, USA",
    help: "One line. Printed as where a shopper mails an arbitration opt-out.",
    type: "text",
    required: true,
  },
  {
    key: "governingLaw",
    label: "Governing law",
    placeholder: "the State of Washington, United States of America",
    help: "Printed after “the laws of” and “a court located in”, so phrase it to follow both.",
    type: "text",
    required: true,
  },
  {
    key: "arbitrationVenue",
    label: "Arbitration venue",
    placeholder: "King County, Washington",
    help: "Where an in-person arbitration would sit, if the parties ever agreed to one.",
    type: "text",
    required: true,
  },
];

function trimmed(legal: StorefrontLegal): StorefrontLegal {
  return {
    legalName: legal.legalName.trim(),
    supportEmail: legal.supportEmail.trim(),
    legalEmail: legal.legalEmail.trim(),
    postalAddress: legal.postalAddress.trim(),
    governingLaw: legal.governingLaw.trim(),
    arbitrationVenue: legal.arbitrationVenue.trim(),
  };
}

function isComplete(legal: StorefrontLegal): boolean {
  return FIELDS.every((field) => !field.required || legal[field.key] !== "");
}

function isSame(a: StorefrontLegal, b: StorefrontLegal): boolean {
  return FIELDS.every((field) => a[field.key] === b[field.key]);
}

/**
 * The legal identity a storefront's Terms of Service and Privacy Policy name, as one form.
 * Shared by the platform default (`/legal`) and an environment's override (Storefront →
 * Legal): the two differ only in what the `footer` offers beside Save. Remount with `key` when
 * the saved value changes, like the loser prize form.
 */
export function LegalIdentityForm({
  legal,
  canEdit,
  saving,
  onSave,
  footer,
}: {
  legal: StorefrontLegal;
  canEdit: boolean;
  saving: boolean;
  onSave: (legal: StorefrontLegal) => void;
  footer?: ReactNode;
}) {
  const [draft, setDraft] = useState<StorefrontLegal>(legal);
  const clean = trimmed(draft);
  const dirty = !isSame(clean, legal);
  const complete = isComplete(clean);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!complete) return;
    onSave(clean);
  };

  return (
    <form className="grid gap-5" onSubmit={submit}>
      <div className="grid gap-4 sm:grid-cols-2">
        {FIELDS.map((field) => (
          <div key={field.key} className="grid gap-2">
            <Label htmlFor={`legal-${field.key}`}>{field.label}</Label>
            <Input
              id={`legal-${field.key}`}
              type={field.type}
              value={draft[field.key]}
              disabled={!canEdit}
              required={field.required}
              onChange={(e) => setDraft({ ...draft, [field.key]: e.target.value })}
              placeholder={field.placeholder}
            />
            <p className="text-xs text-muted-foreground">{field.help}</p>
          </div>
        ))}
      </div>
      {canEdit && (
        <div className="flex flex-wrap items-center gap-2">
          <Button type="submit" disabled={saving || !dirty || !complete}>
            {saving ? "Saving…" : "Save legal identity"}
          </Button>
          {footer}
        </div>
      )}
    </form>
  );
}
